import type { LanguageCode, StatusCode, Topic } from '../../../backend/main';
import { createNonNullableTextControl } from '../form-controls/create-text-control';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { createPctFormGroup } from '../form-controls/create-pct-form-group';
import { ValidatorConstants } from '../../constants/validator.constants';
import { TypeArrayConstants } from '../../constants/type-arrays.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { DestroyRef } from '@angular/core';
import type { PctFormGroup } from '@pct/ng-form';

interface Translation {
  language: LanguageCode;
  name: string;
}

export interface CategoryDetailsFormValues {
  status: StatusCode;
  translations: Translation[];
}

export interface CategoryTranslationFormGroup {
  language: FormControl<LanguageCode>;
  name: FormControl<string>;
}

export interface CategoryDetailsFormControls {
  status: FormControl<StatusCode>;
  translations: FormArray<FormGroup<CategoryTranslationFormGroup>>;
}

export class CategoryDetailsForm {
  readonly statusControl = new FormControl<StatusCode>('Active', {
    nonNullable: true,
    validators: [Validators.required],
  });

  readonly translationsFormArray = new FormArray<FormGroup<CategoryTranslationFormGroup>>(
    TypeArrayConstants.LANGUAGE_CODES.map(languageCode =>
      this.buildTranslationFormGroup(languageCode),
    ),
  );
  readonly topicsControl: FormControl<Topic[]> | undefined = undefined;

  protected readonly builtFormGroup = createPctFormGroup({
    status: this.statusControl,
    translations: this.translationsFormArray,
  });

  formGroup(): PctFormGroup {
    return this.builtFormGroup;
  }

  disableForm(): void {
    this.builtFormGroup.disable();
  }

  enableForm(): void {
    this.builtFormGroup.enable();
  }

  subscribeToTranslationsValueChanges(
    translationControlsParam: CategoryTranslationFormGroup['name'][],
    destroyRef: DestroyRef,
  ): void {
    const translationControls = translationControlsParam;
    translationControls.forEach(control =>
      control.valueChanges
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe(() => this.validateAllFields(translationControls)),
    );
  }

  private buildTranslationFormGroup(
    translationCode: LanguageCode,
  ): FormGroup<CategoryTranslationFormGroup> {
    return new FormGroup<CategoryTranslationFormGroup>({
      language: new FormControl(translationCode, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      name: createNonNullableTextControl(
        [Validators.required, Validators.minLength(ValidatorConstants.TITLE_MIN_LENGTH)],
        ValidatorConstants.CATEGORY_NAME_MAX_LENGTH,
      ),
    });
  }

  private validateAllFields(translationControls: CategoryTranslationFormGroup['name'][]): void {
    const hasFilledName = translationControls.some(c => c.value.trim().length > 0);
    translationControls.forEach(c => this.handleFieldValidation(hasFilledName, c));
  }

  private handleFieldValidation(
    hasFilledName: boolean,
    control: CategoryTranslationFormGroup['name'],
  ): void {
    if (hasFilledName) {
      control.removeValidators(Validators.required);
    } else {
      control.addValidators(Validators.required);
    }
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  }
}
